import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, api, formatDateId, formatIdr } from '../api/client';
import { Alert, Button, Card, Spinner } from '../components/ui';
import { useConfirm } from '../components/confirm';
import { useT } from '../i18n/locale';
import type { TranslationKey } from '../i18n/locale';
import { useAdminSession } from './session';
import { TENANT_STATUS_KEY } from './TenantsScreen';

interface Detail {
  tenant: {
    id: string;
    name: string;
    slug: string;
    status: string;
    billing_email: string | null;
    created_at: string;
    deletion_scheduled_for: string | null;
  };
  users: Array<{
    id: string;
    email: string;
    name: string;
    role: string;
    is_active: boolean;
    email_verified_at: string | null;
    last_login_at: string | null;
  }>;
  subscription: {
    plan_code: string;
    status: string;
    monthly_rows: number;
    max_users: number | null;
    period_end: string;
    price_idr: number;
  } | null;
  invoices: Array<{
    number: string;
    plan_code: string;
    total_idr: number;
    status: string;
    created_at: string;
  }>;
  databases: Array<{ id: string; alias: string; aol_db_id: string; is_active: boolean }>;
  audit: Array<{
    action: string;
    user_email: string | null;
    created_at: string;
    by_platform: boolean;
  }>;
}

/** Raw billing/subscription status → label key, with a raw-string fallback. */
const BILLING_STATUS_KEY: Record<string, TranslationKey> = {
  trialing: 'billingStatus.trialing',
  active: 'billingStatus.active',
  past_due: 'billingStatus.pastDue',
  cancelled: 'billingStatus.cancelled',
  paid: 'billingStatus.paid',
  pending: 'billingStatus.pending',
  failed: 'billingStatus.failed',
  expired: 'billingStatus.expired',
  refunded: 'billingStatus.refunded',
};

export default function TenantDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const { isSuper } = useAdminSession();
  const confirm = useConfirm();
  const t = useT();

  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setData(await api.get<Detail>(`/platform/tenants/${id}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('admin.tenantsLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(message);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('admin.actionFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner label={t('admin.loadingTenants')} />;
  if (!data) return <div className="p-4">{error ? <Alert tone="error">{error}</Alert> : null}</div>;

  const ten = data.tenant;
  const suspended = ten.status === 'suspended';
  const closing = Boolean(ten.deletion_scheduled_for);
  const tenantStatusKey = TENANT_STATUS_KEY[ten.status];
  const statusLabel = tenantStatusKey ? t(tenantStatusKey) : ten.status;
  const subStatusKey = data.subscription ? BILLING_STATUS_KEY[data.subscription.status] : undefined;

  return (
    <div className="mx-auto w-full max-w-5xl p-4">
      {error ? <Alert tone="error">{error}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <Card className="mb-3 flex flex-wrap items-start justify-between gap-3 p-4">
        <div>
          <h2 className="m-0 text-lg font-black text-slate-900">{ten.name}</h2>
          <p className="m-0 mt-1 text-sm text-slate-500">
            {t('admin.metaLine', {
              email: ten.billing_email ?? '-',
              date: formatDateId(ten.created_at),
            })}
            <b>{statusLabel}</b>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {closing ? (
            isSuper ? (
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => api.post(`/platform/tenants/${ten.id}/delete/cancel`),
                    t('admin.deletionCancelled'),
                  )
                }
              >
                {t('admin.cancelDeletion')}
              </Button>
            ) : null
          ) : (
            <Button
              variant={suspended ? 'ghost' : 'danger'}
              disabled={busy}
              onClick={async () => {
                if (!suspended) {
                  const ok = await confirm({
                    title: t('admin.suspendTitle', { name: ten.name }),
                    tone: 'danger',
                    confirmLabel: t('admin.suspend'),
                    body: t('admin.suspendBody'),
                  });
                  if (!ok) return;
                }
                await act(
                  () => api.patch(`/platform/tenants/${ten.id}`, { suspended: !suspended }),
                  suspended ? t('admin.suspensionLifted') : t('admin.tenantSuspended'),
                );
              }}
            >
              {suspended ? t('admin.liftSuspension') : t('admin.suspend')}
            </Button>
          )}

          {/* Deletion is superadmin-only; the button is absent rather than
              present-and-refused for an ordinary admin. */}
          {isSuper && !closing ? (
            <Button
              variant="danger"
              disabled={busy}
              onClick={async () => {
                const ok = await confirm({
                  title: t('admin.scheduleDeleteTitle', { name: ten.name }),
                  tone: 'danger',
                  confirmLabel: t('admin.scheduleDelete'),
                  body: t('admin.scheduleDeleteBody'),
                });
                if (!ok) return;
                await act(
                  () =>
                    api.post(`/platform/tenants/${ten.id}/delete`, {
                      reason: t('admin.deleteReason'),
                    }),
                  t('admin.deletionScheduled'),
                );
              }}
            >
              {t('admin.deleteTenant')}
            </Button>
          ) : null}
        </div>
      </Card>

      {closing ? (
        <Alert tone="warn">
          {t('admin.scheduledPermanent', {
            date: formatDateId(ten.deletion_scheduled_for!),
          })}
        </Alert>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        <Card className="p-4">
          <h3 className="m-0 mb-2 text-sm font-bold text-slate-800">{t('admin.subscription')}</h3>
          {data.subscription ? (
            <dl className="m-0 grid grid-cols-2 gap-1 text-sm">
              <dt className="text-slate-500">{t('admin.colPlan')}</dt>
              <dd className="m-0 font-semibold">{data.subscription.plan_code}</dd>
              <dt className="text-slate-500">{t('admin.colStatus')}</dt>
              <dd className="m-0 font-semibold">
                {subStatusKey ? t(subStatusKey) : data.subscription.status}
              </dd>
              <dt className="text-slate-500">{t('admin.rowsPerMonth')}</dt>
              <dd className="m-0">{data.subscription.monthly_rows}</dd>
              <dt className="text-slate-500">{t('admin.ends')}</dt>
              <dd className="m-0">{formatDateId(data.subscription.period_end)}</dd>
            </dl>
          ) : (
            <p className="m-0 text-sm text-slate-500">{t('admin.noSubscription')}</p>
          )}
        </Card>

        <Card className="p-4">
          <h3 className="m-0 mb-2 text-sm font-bold text-slate-800">{t('admin.databases')}</h3>
          {data.databases.length === 0 ? (
            <p className="m-0 text-sm text-slate-500">{t('admin.notConnected')}</p>
          ) : (
            <ul className="m-0 list-none p-0 text-sm">
              {data.databases.map((d) => (
                <li key={d.id} className="py-0.5">
                  <b>{d.alias}</b>{' '}
                  <span className="text-slate-500">#{d.aol_db_id}</span>
                  {d.is_active ? null : (
                    <span className="ml-1 text-slate-400">{t('admin.released')}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="mt-3 overflow-hidden">
        <h3 className="m-0 px-4 pt-4 pb-2 text-sm font-bold text-slate-800">{t('admin.users')}</h3>
        <table className="w-full border-collapse text-left">
          <thead className="border-y border-slate-200 bg-slate-100/60 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
            <tr>
              <th className="px-4 py-2">{t('team.colName')}</th>
              <th className="px-3 py-2">{t('team.colEmail')}</th>
              <th className="px-3 py-2">{t('team.colRole')}</th>
              <th className="px-3 py-2">{t('team.colLastLogin')}</th>
              <th className="px-4 py-2 text-right">{t('team.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100">
                <td className="px-4 py-2 text-sm font-bold text-slate-900">
                  {u.name}
                  {u.is_active ? null : (
                    <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
                      {t('admin.inactiveBadge')}
                    </span>
                  )}
                  {u.email_verified_at ? null : (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                      {t('admin.unverifiedBadge')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-sm text-slate-600">{u.email}</td>
                <td className="px-3 py-2 text-sm text-slate-600">{u.role}</td>
                <td className="px-3 py-2 text-sm text-slate-500">
                  {u.last_login_at ? formatDateId(u.last_login_at) : t('common.never')}
                </td>
                <td className="px-4 py-2 text-right">
                  <Button
                    variant={u.is_active ? 'danger' : 'ghost'}
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => api.patch(`/platform/users/${u.id}`, { isActive: !u.is_active }),
                        u.is_active ? t('admin.userSuspended') : t('admin.userActivated'),
                      )
                    }
                  >
                    {u.is_active ? t('admin.suspend') : t('team.activate')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <Card className="p-4">
          <h3 className="m-0 mb-2 text-sm font-bold text-slate-800">{t('admin.invoices')}</h3>
          {data.invoices.length === 0 ? (
            <p className="m-0 text-sm text-slate-500">{t('admin.noInvoices')}</p>
          ) : (
            <ul className="m-0 list-none p-0">
              {data.invoices.map((inv) => {
                const invStatusKey = BILLING_STATUS_KEY[inv.status];
                return (
                <li
                  key={inv.number}
                  className="flex items-center justify-between gap-2 border-b border-slate-100 py-1.5 text-sm last:border-0"
                >
                  <span className="min-w-0 flex-1 truncate">
                    <b>{inv.number}</b>{' '}
                    <span className="text-slate-500">
                      {inv.plan_code} · {formatIdr(inv.total_idr)}
                    </span>
                  </span>
                  <span className="text-xs text-slate-500">
                    {invStatusKey ? t(invStatusKey) : inv.status}
                  </span>
                  {isSuper && inv.status !== 'paid' ? (
                    <Button
                      disabled={busy}
                      onClick={() =>
                        void act(
                          () => api.post(`/platform/invoices/${inv.number}/mark-paid`),
                          t('admin.invoiceMarkedPaid', { number: inv.number }),
                        )
                      }
                    >
                      {t('admin.markPaid')}
                    </Button>
                  ) : null}
                </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card className="p-4">
          <h3 className="m-0 mb-2 text-sm font-bold text-slate-800">{t('admin.recentActivity')}</h3>
          <ul className="m-0 list-none p-0 text-sm">
            {data.audit.map((a, i) => (
              <li key={i} className="border-b border-slate-100 py-1 last:border-0">
                <span className="font-semibold text-slate-700">{a.action}</span>
                {/* Marks actions taken from this console rather than by the
                    customer, so "who suspended us?" is answerable. */}
                {a.by_platform ? (
                  <span className="ml-1 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    platform
                  </span>
                ) : null}
                <span className="ml-1 text-slate-500">
                  {a.user_email ?? '-'} · {formatDateId(a.created_at)}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
